import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AnvandareOrganisation } from 'src/app/auth/services/auth.model';
import { SessionService } from 'src/app/auth/services/session.service';
import { isValidPersonnummer } from 'src/app/shared/helpers/form-validation/custom-form-validatiors';
import {
  AnvandarRoll,
  AnvandareDTO,
  FLAT_ROLES,
  OrganisationDTO,
  Role,
  RoleLabels,
} from '../anvandare/anvandare.models';
import { AnvandareService } from '../anvandare/anvandare.service';
import { AnvandarRollCheck } from '../anvandare/edit-anvandare/edit-anvandare.component';
import { randomString } from 'src/app/shared/helpers/auth-utils/auth.utils';

export enum AnvandareTyp {
  Intern = 'Intern',
  Extern = 'Extern',
}

@Component({
  selector: 'lib-lagga-till-anvandare',
  templateUrl: './lagga-till-anvandare.component.html',
  styleUrls: ['./lagga-till-anvandare.component.scss'],
  standalone: false,
})
export class LaggaTillAnvandareComponent implements OnInit {
  protected orgId?: string;
  protected userOrgName?: string;
  protected isInternUser: boolean = false;
  protected chosenOrg?: string;

  constructor(private sessionService: SessionService) {}
  private fb = inject(FormBuilder);
  private anvandareService = inject(AnvandareService);
  private router = inject(Router);
  private destroy = inject(DestroyRef);
  private messageService = inject(MessageService);

  protected defaultSelect = { namn: '-- Välj --' };
  protected roles = FLAT_ROLES;
  private organisations!: OrganisationDTO[];
  protected filteredOrganisations!: OrganisationDTO[];

  protected canSave = false;
  protected filteredRoles: string[] = this.roles.slice();

  private internRoles = ['SR15', 'SR16', 'SR21', 'SR20'];
  private externRoles = ['SR22', 'SR25', 'SR26'];

  protected errorMsg: boolean = false;

  protected creatAnvandareForm = this.fb.nonNullable.group({
    anvandarnamn: ['', Validators.required],
    anvandartyp: [''],
    efternamn: ['', Validators.required],
    epost: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')]],
    fornamn: ['', Validators.required],
    organisationId: [this.defaultSelect.namn],
    titel: [''],
    status: '',
    telefon: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
    roller: this.fb.nonNullable.group({}),
    anvandareOrganisationer: this.fb.array([]),
  });

  usernamePlaceholder: string = '';
  epostPlaceholder: string = 'Format: förnamn.efternamn@företagsnamn.se';
  mobilPlaceHolder: string = 'Format: XXXXXXXXXX';

  ngOnInit(): void {
    this.isInternUser = this.sessionService.isInternUser;
    this.anvandareService
      .getAllOrganisations()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((orgs) => {
        this.organisations = orgs
          .filter((org) => org.labKod?.includes('SE') || org.labKod === 'FOHML')
          .map((org) => {
            return {
              ...org,
              label: `${org.labKod} - ${org.namn}`,
            };
          })
          .sort((a, b) => (a.labKod < b.labKod ? -1 : 1));
        this.userOrgName = this.organisations.find((org) => org.id === this.sessionService.getOrgId())?.namn;
      });

    this.filteredRoles.forEach((roll) => {
      this.creatAnvandareForm.controls.roller.addControl(roll, this.fb.control(false));
    });

    FLAT_ROLES.forEach((roll) => {
      this.creatAnvandareForm.controls.roller.addControl(roll, this.fb.control(false));
    });
    this.creatAnvandareForm.valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe((change) => {
      this.updateCanSave();
    });
    this.orgId = this.sessionService.getOrgId();
    this.updateUsernamePlaceholder();

    this.creatAnvandareForm.controls.organisationId.valueChanges
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((orgId) => {
        this.chosenOrg = this.organisations.find((org) => org.id === orgId)?.namn;
        this.anvandarRoller.forEach((roll) => (roll.organisationId = orgId));
        this.filteredAnvandarRoller.forEach((roll) => (roll.organisationId = orgId));
      });
    this.initAnvandarRoller();
    if (!this.isInternUser) {
      this.filterRoles(AnvandareTyp.Extern);
    }
    if(!this.isInternUser) {
      this.creatAnvandareForm.controls.anvandarnamn.setValidators([
        Validators.required,
        Validators.minLength(12),
        Validators.maxLength(12),
        isValidPersonnummer,
      ]);
    }
  }

  anvandarRoller: AnvandarRollCheck[] = [];
  filteredAnvandarRoller: AnvandarRollCheck[] = [];

  initAnvandarRoller() {
    FLAT_ROLES.forEach((role) => {
      this.anvandarRoller.push({
        organisationId: this.creatAnvandareForm.controls.organisationId.value,
        roll: role,
        checked: false,
        show: true,
      });
      this.filteredAnvandarRoller.push({
        organisationId: this.creatAnvandareForm.controls.organisationId.value,
        roll: role,
        checked: false,
        show: true,
      });
    });
  }

  filterRoles(anvandarTyp?: string): void {
    this.filteredRoles = anvandarTyp === AnvandareTyp.Intern ? this.internRoles : this.externRoles;
    this.filteredAnvandarRoller =
      anvandarTyp === AnvandareTyp.Intern
        ? [...this.anvandarRoller.filter((roll) => this.internRoles.includes(roll.roll))]
        : [...this.anvandarRoller.filter((roll) => this.externRoles.includes(roll.roll))];
  }

  onAnvandartypChange(): void {
    this.creatAnvandareForm.controls.anvandarnamn.clearValidators();
    this.creatAnvandareForm.controls.organisationId.reset();
    this.chosenOrg = undefined;

    const anvandartyp = this.creatAnvandareForm.controls.anvandartyp.value;
    if (anvandartyp == AnvandareTyp.Intern) {
      this.filteredOrganisations = this.organisations.filter((org) => org.labKod === 'FOHML');
      this.creatAnvandareForm.controls.anvandarnamn.setValidators([Validators.required, Validators.maxLength(50)]);
    }
    if (anvandartyp == AnvandareTyp.Extern) {
      this.filteredOrganisations = this.organisations.filter((org) => org.labKod?.includes('SE'));

      this.creatAnvandareForm.controls.anvandarnamn.setValidators([
        Validators.required,
        Validators.minLength(12),
        Validators.maxLength(12),
        isValidPersonnummer,
      ]);
    }
    this.creatAnvandareForm.controls.anvandarnamn.updateValueAndValidity();

    this.filterRoles(anvandartyp);

    this.updateUsernamePlaceholder();
  }

  getRoleLabel(roll: string): string {
    return RoleLabels[roll as Role] || roll;
  }

  updateCanSave() {
    this.canSave = !this.creatAnvandareForm.invalid;
  }

  save() {
    this.errorMsg = false;

    const userOrganisationId = this.sessionService.getOrgId();
    if (!userOrganisationId) {
      throw new Error('Organisation is missing from the session.');
    }

    const orgId = !this.isInternUser ? userOrganisationId : this.creatAnvandareForm.controls.organisationId.value;

    const userRoles: AnvandarRoll[] = this.filteredAnvandarRoller
      .filter((roll) => roll.checked)
      .map((roll) => {
        return {
          organisationId: orgId,
          roll: roll.roll,
        };
      });



    const orgName = this.organisations.find((org) => org.id === orgId)?.namn as string;

    const anvandareOrganisation: AnvandareOrganisation = {
      id: randomString(40),
      epost: this.creatAnvandareForm.controls.epost.value ?? '',
      organisation: orgName ?? '',
      organisationId: orgId ?? '',
      telefon: this.creatAnvandareForm.controls.telefon.value ?? '',
      titel: this.creatAnvandareForm.controls.titel.value ?? '',
    };
    const createdAnvandare: AnvandareDTO = {
      ...(this.creatAnvandareForm.value as AnvandareDTO),
      roller: [],
      anvandarRoller: userRoles,
      organisationId: orgId,
      organisation: orgName,
      anvandartyp: this.isInternUser ? this.creatAnvandareForm.controls.anvandartyp.value : 'Extern',
      anvandareOrganisationer: [anvandareOrganisation],
      theChosenOrgId: orgId
    };

    this.anvandareService.createAnvandare(createdAnvandare).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          detail: 'Användare har registrerats',
        });
        this.router.navigate(['/anvandare']);
      },
      error: (e: any) => {
        this.messageService.add({
          severity: 'error',
          detail: 'Något gick fel vid registrering av användare',
        });
        this.errorMsg = true;
      },
    });
  }

  protected updateUsernamePlaceholder(): void {
    const anvandartyp = this.creatAnvandareForm.controls.anvandartyp.value;
    this.usernamePlaceholder =
      !this.isInternUser || anvandartyp === AnvandareTyp.Extern
        ? 'YYYYMMDDxxxx'
        : anvandartyp === AnvandareTyp.Intern
          ? 'Förnamn.Efternamn'
          : '';
  }

  protected cancel() {
    this.router.navigate(['/anvandare']);
  }
}
